// The in-world per-bee B++ state label (WAG-36, `bee-state-flash`; capability
// `client-bee-state-labels`). Pure and Pixi-free, like `bee-bars.ts` and `pick.ts`: this
// decides WHICH labels are live, their alpha from the fade curve, where each sits relative to
// its bee's ground origin, who survives a crowd, and what a pick means;
// `CanvasRenderer.drawStateLabels` batches the paint over per-name cached textures and is
// device-exercised only (repo convention D-086).
//
// Everything here is presentation. The trace records *that* a bee's state changed at a tick;
// how long the resulting label lingers is a wall-clock concern on the same side of the line as
// corpse fade, and `waggle-sim` determinism is entirely uninvolved.
//
// Three rules are easy to get subtly wrong and are therefore stated in code as well as spec:
//
//   1. Liveness is decided by AGE, not by alpha (design E6). A label is born at age 0 where the
//      fade-in curve is legitimately 0, so an `alpha <= 0` liveness test would destroy every
//      label on the frame it was created.
//   2. A state change is a VALUE comparison against the previously decoded state, never field
//      presence (design E10). Keyframes restate every section whole at cadence 15, so
//      presence-triggering would flash every bee every 15 ticks — wrong in a way that looks
//      right at 1x on a short trace.
//   3. A pick clears on the bee's fate in the TRACE, never on its absence from the frame on
//      screen (design E9). The transport is freely draggable, so a frame-absence rule would
//      silently discard the pick on one drag.

import { depthScale, spriteCameraScale } from './projection';

/** Fade-in (ms). Prototyped: `prototypes.md` §"Pass 1" B. */
export const STATE_LABEL_FADE_IN_MS = 120;
/** Hold at full opacity (ms). */
export const STATE_LABEL_HOLD_MS = 640;
/** Fade-out (ms). */
export const STATE_LABEL_FADE_OUT_MS = 240;

/** One label's whole life: **one second of WALL-CLOCK time**, at every playback speed. One
 * second of *sim* time would be 62 ms of real time at 16x, and a label that cannot be read is
 * not a feature — which is why playback speed is not an input to this module at all. */
export const STATE_LABEL_LIFETIME_MS =
  STATE_LABEL_FADE_IN_MS + STATE_LABEL_HOLD_MS + STATE_LABEL_FADE_OUT_MS;

/** How long a live label holds its name before a newer state may replace it. At 16x a bee can
 * transition ~80 times per wall-clock second, and without a floor every transition would
 * restart the fade-in and the label would strobe. It MUST be at least the fade-in or a label
 * could be replaced before it had finished appearing; 260 against 120 leaves 140 ms at full
 * opacity first. A transition inside the floor is **dropped, not queued** — a queued label
 * shows a state the bee left seconds ago. */
export const STATE_LABEL_MIN_VISIBLE_MS = 260;

/** The authored font size (px at scale 1): the design kit's existing `--font-mono` size,
 * unchanged. Load-bearing rather than cosmetic — the label-to-bee ratio is
 * `base / 24` and is camera-INVARIANT, so the authored base alone sets crowding at every zoom.
 * (A 20 px base was a superseded pass-3 answer; it more than doubles that ratio and so discards
 * the density evidence overlap suppression rests on. Design E7.) */
export const STATE_LABEL_FONT_PX = 10;

/** Advance width of one monospace glyph at {@link STATE_LABEL_FONT_PX} (0.6 em, the usual mono
 * ratio). The collision rectangle is measured with it, and the renderer's cached texture is
 * laid out with it, so the bound describes the label actually drawn. */
export const STATE_LABEL_CHAR_WIDTH_PX = 6;

/** Half-width of the paper halo (px at scale 1, per side), design **E12**. The renderer strokes
 * the halo OUTSIDE the glyph box, so it is part of what a viewer sees and therefore part of what
 * the crowd bound must describe: measuring the bare glyphs while painting glyphs-plus-halo is the
 * same class of error as measuring unscaled while drawing scaled, one layer smaller. The pass-1
 * sketch's own `placeLabel` included it for this reason. Kept in step with the renderer's stroke
 * width (`max(1, FONT_PX x 0.3)`) and its texture padding (`max(2, FONT_PX x 0.3)`). */
export const STATE_LABEL_HALO_PAD_PX = Math.max(2, STATE_LABEL_FONT_PX * 0.3);

/** How far BELOW the bee's ground origin (px at scale 1) the label's top edge sits. WAG-36 asks
 * for the state "under the bee", and the health/nectar bars own the band above it
 * (`BEE_BAR_RISE_PX`, negative offsets) — the two must never compete for the same space. */
export const STATE_LABEL_DROP_PX = 6;

/**
 * The camera scale below which labels are suppressed — **its own constant**, deliberately not
 * an alias of `BEE_BAR_MIN_CAMERA_SCALE` (0.9). That one is derived from when 2 px bars smear
 * into a band; this one from a legibility knee: the label scales with the camera like every
 * sprite (`spriteCameraScale(s) = s / 2`) times the tilt plane's 0.85–1.25 depth, so requiring
 * at least 8.5 px at the BACK of the plane gives `10 x (z / 2) x 0.85 >= 8.5`, i.e. `z >= 2.0`
 * — exactly `SPRITE_NATIVE_ZOOM`. The rule reads: *labels are legible at or above the zoom
 * where sprites render at native art size.*
 *
 * The two floors differing widely is intended, not an oversight: between 0.9 and 2.0 a bee is
 * barred and unlabelled, and that band is precisely where a label would be present but
 * unreadable — worse than absent, because it costs attention and returns nothing. The shipped
 * opening framing is the tested `colonyFitZoom` 3.08, so the feature is on without touching the
 * camera. (Design E7; provisional under UI.md §10.)
 */
export const STATE_LABEL_MIN_CAMERA_SCALE = 2;

/** A label's opacity at `ageMs` of wall-clock age: the 120 / 640 / 240 ramp. Total — a
 * negative, non-finite or past-lifetime age yields 0 rather than NaN. Note this is NOT the
 * liveness test (design E6): a newborn label is legitimately at 0. */
export function stateLabelAlpha(ageMs: number): number {
  if (!Number.isFinite(ageMs) || ageMs <= 0) return 0;
  if (ageMs < STATE_LABEL_FADE_IN_MS) return ageMs / STATE_LABEL_FADE_IN_MS;
  const fadeOutStart = STATE_LABEL_FADE_IN_MS + STATE_LABEL_HOLD_MS;
  if (ageMs <= fadeOutStart) return 1;
  if (ageMs >= STATE_LABEL_LIFETIME_MS) return 0;
  return 1 - (ageMs - fadeOutStart) / STATE_LABEL_FADE_OUT_MS;
}

/** Whether labels are legible at this camera scale (monotone in `scale`, so a zoom gesture
 * never flickers them). A degenerate camera — non-finite, zero or negative — hides them rather
 * than dividing by it. */
export function stateLabelsVisibleAtScale(scale: number): boolean {
  if (!Number.isFinite(scale) || scale <= 0) return false;
  return scale >= STATE_LABEL_MIN_CAMERA_SCALE;
}

/** One bee as the label layer sees it: its identity, its colony (for the subject gate), its
 * resolved B++ state name — `null` for the trace's no-state sentinel, which never emits — and
 * its **drawn** ground origin in screen px (flower attachment and interpolation already
 * applied by the caller, so a label tracks the bee the renderer actually drew). */
export interface StateLabelBee {
  readonly id: number;
  readonly colonyId: number;
  readonly stateName: string | null;
  readonly screenX: number;
  readonly screenY: number;
}

export interface StateLabelFrameInput {
  /** Wall-clock milliseconds (the RAF timestamp), never a sim tick. */
  readonly nowMs: number;
  readonly bees: readonly StateLabelBee[];
  readonly cameraScale: number;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  /** The review subject colony, or `null` for no gate (the Sandbox screen's own colony makes
   * the gate a no-op there). */
  readonly subjectColonyId: number | null;
  /** True while the playhead is being DRAGGED: crowd labels are suppressed, and no transition
   * seen during the drag is emitted afterwards. A picked bee is exempt. */
  readonly scrubbing?: boolean;
  /** True on the first frame after a DISCONTINUOUS playhead jump: live labels are cleared and
   * the landing frame's states become the new baseline, silently. Without it a seek would fire
   * a mass flash of transitions that happened during the drag. */
  readonly seek?: boolean;
  /** Whether the picked bee appears anywhere in the REMAINDER of the trace. Consulted only when
   * the picked bee is absent from this frame: `true` (the default when the caller supplies no
   * lookup) leaves the pick dormant, `false` clears it. Absence at the playhead is not absence
   * from the trace (design E9). */
  readonly pickedBeeAppearsLater?: (beeId: number) => boolean;
}

/** A label the renderer should paint this frame, at its final drawn geometry. */
export interface PlacedStateLabel {
  readonly beeId: number;
  readonly name: string;
  readonly alpha: number;
  /** Horizontal CENTRE, always the bee's own drawn x — a skipped label is never relocated. */
  readonly screenX: number;
  /** Top edge, below the bee's ground origin. */
  readonly screenY: number;
  /** `spriteCameraScale(cameraScale) * depthScale` — the label scales with the camera like
   * every other sprite, because it is attached to a bee and so belongs in world space. */
  readonly scale: number;
  /** Drawn width, glyph box PLUS the halo on both sides, at the drawn scale — this is the
   * rectangle overlap suppression measures, so it describes what is painted, halo included. */
  readonly widthPx: number;
  /** Drawn height, glyph box plus the halo above and below, at the drawn scale. */
  readonly heightPx: number;
  readonly picked: boolean;
}

export interface StateLabelFrameResult {
  /** Draw order: the picked bee first (it owns its space), then nearest-to-camera-centre. */
  readonly labels: readonly PlacedStateLabel[];
  readonly drawn: number;
  /** Labels that were live but collided with one already placed. Reported so a "we labelled
   * everything" reading is not available to a reader of the code or a test. */
  readonly suppressed: number;
  /** Bee ids whose label was started or restarted this frame. */
  readonly emitted: readonly number[];
  /** Transitions discarded by the minimum-visible-duration floor. */
  readonly dropped: number;
}

interface LiveLabel {
  name: string;
  bornMs: number;
}

interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

function overlaps(a: Rect, b: Rect): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

/**
 * The per-bee label state across frames: the decoded-state baseline each change is compared
 * against, the live labels and their birth times, and the picked bee.
 *
 * Deliberately a small mutable object rather than a pure function of a caller-held state blob:
 * the layer is per-canvas, its state is entirely internal, and every decision it makes is still
 * a pure function of (state, frame) — which is what makes all of it testable without a GPU.
 */
export class StateLabelLayer {
  private readonly baseline = new Map<number, string | null>();
  private readonly live = new Map<number, LiveLabel>();
  private picked: number | null = null;

  /** The picked bee, or `null`. Read back after a frame: a pick whose bee has left the trace
   * clears itself, and clearing is a one-way event. */
  get pickedBeeId(): number | null {
    return this.picked;
  }

  setPickedBee(beeId: number | null): void {
    this.picked = beeId;
  }

  /** Drop every baseline, live label and the pick — on trace load and on version switch, where
   * neither the bee ids nor the state names carry over. */
  reset(): void {
    this.baseline.clear();
    this.live.clear();
    this.picked = null;
  }

  frame(input: StateLabelFrameInput): StateLabelFrameResult {
    const now = input.nowMs;
    const scrubbing = input.scrubbing === true;
    const seek = input.seek === true;
    const emitted: number[] = [];
    let dropped = 0;

    if (seek) {
      // Re-baseline silently: the landing frame's states are adopted as the new baseline and
      // nothing is emitted, because those transitions happened during the drag.
      this.baseline.clear();
      this.live.clear();
    }

    // Expire by AGE, before emission, so a floor comparison is never made against a corpse.
    for (const [beeId, label] of this.live) {
      if (now - label.bornMs >= STATE_LABEL_LIFETIME_MS) {
        this.live.delete(beeId);
      }
    }

    const present = new Map<number, StateLabelBee>();
    for (const bee of input.bees) {
      present.set(bee.id, bee);
      const previous = this.baseline.get(bee.id);
      this.baseline.set(bee.id, bee.stateName);
      if (seek || scrubbing) continue;
      // A value comparison, never field presence — and a bee whose state resolves to the
      // sentinel never emits, in either direction (a first sighting is not a transition).
      if (bee.stateName === null || previous === undefined || previous === null) continue;
      if (previous === bee.stateName) continue;

      const label = this.live.get(bee.id);
      if (label === undefined) {
        this.live.set(bee.id, { name: bee.stateName, bornMs: now });
        emitted.push(bee.id);
      } else if (now - label.bornMs >= STATE_LABEL_MIN_VISIBLE_MS) {
        label.name = bee.stateName;
        label.bornMs = now;
        emitted.push(bee.id);
      } else {
        dropped += 1; // inside the floor: dropped, not queued
      }
    }

    if (scrubbing) {
      this.live.clear();
    }

    // A pick clears only when its bee is gone from the REMAINDER of the trace; absent-but-later
    // is dormant, and clearing is one-way (a backward seek does not resurrect it).
    if (this.picked !== null && !present.has(this.picked)) {
      const appearsLater = input.pickedBeeAppearsLater?.(this.picked) ?? true;
      if (!appearsLater) this.picked = null;
    }

    if (!stateLabelsVisibleAtScale(input.cameraScale)) {
      return { labels: [], drawn: 0, suppressed: 0, emitted, dropped };
    }

    const labels: PlacedStateLabel[] = [];
    const occupied: Rect[] = [];

    // The picked bee is placed FIRST so it owns its space, is labelled with its CURRENT state
    // continuously rather than only on change, and is exempt from both the subject gate and
    // collision suppression. The zoom floor still applies.
    const pickedBee = this.picked === null ? undefined : present.get(this.picked);
    if (pickedBee !== undefined && pickedBee.stateName !== null) {
      const placed = this.place(pickedBee, pickedBee.stateName, 1, true, input);
      if (placed !== null) {
        labels.push(placed);
        occupied.push(rectOf(placed));
      }
    }

    let suppressed = 0;
    if (!scrubbing) {
      const centreX = input.viewportWidth / 2;
      const centreY = input.viewportHeight / 2;
      const candidates = input.bees
        .filter(
          (bee) =>
            bee.id !== this.picked &&
            this.live.has(bee.id) &&
            (input.subjectColonyId === null || bee.colonyId === input.subjectColonyId),
        )
        // Nearest to the camera centre first — stated rather than inherited from the entity
        // list's y-sorted order, which would silently privilege whichever bee is furthest
        // north. Ties break on the lowest id, as every proximity query in this codebase does.
        .sort((a, b) => {
          const da = Math.hypot(a.screenX - centreX, a.screenY - centreY);
          const db = Math.hypot(b.screenX - centreX, b.screenY - centreY);
          return da === db ? a.id - b.id : da - db;
        });

      for (const bee of candidates) {
        const label = this.live.get(bee.id);
        if (label === undefined) continue;
        const placed = this.place(bee, label.name, stateLabelAlpha(now - label.bornMs), false, input);
        if (placed === null) continue;
        const rect = rectOf(placed);
        // Skipped, never nudged: a moved label is no longer under the bee it describes, and
        // "which bee is this about?" is the only question a label exists to answer. The
        // occupied set is rebuilt every frame, so a suppressed label returns unaided.
        if (occupied.some((other) => overlaps(rect, other))) {
          suppressed += 1;
          continue;
        }
        labels.push(placed);
        occupied.push(rect);
      }
    }

    return { labels, drawn: labels.length, suppressed, emitted, dropped };
  }

  /** Final drawn geometry for one label, or `null` if the bee's position is not finite. */
  private place(
    bee: StateLabelBee,
    name: string,
    alpha: number,
    picked: boolean,
    input: StateLabelFrameInput,
  ): PlacedStateLabel | null {
    if (!Number.isFinite(bee.screenX) || !Number.isFinite(bee.screenY)) return null;
    const viewportHeight =
      Number.isFinite(input.viewportHeight) && input.viewportHeight > 0 ? input.viewportHeight : 1;
    // The same two factors the renderer scales a bee sprite by, in the same order.
    const scale = spriteCameraScale(input.cameraScale) * depthScale(bee.screenY, viewportHeight);
    return {
      beeId: bee.id,
      name,
      alpha,
      screenX: bee.screenX,
      screenY: bee.screenY + STATE_LABEL_DROP_PX * scale,
      scale,
      // Measured at the DRAWN size, camera and depth included, and inclusive of the halo the
      // renderer strokes outside the glyph box: measuring unscaled while drawing scaled would
      // make the crowd bound describe a label nobody sees, and measuring bare glyphs while
      // painting a halo is the same error one layer smaller.
      widthPx: (name.length * STATE_LABEL_CHAR_WIDTH_PX + 2 * STATE_LABEL_HALO_PAD_PX) * scale,
      heightPx: (STATE_LABEL_FONT_PX + 2 * STATE_LABEL_HALO_PAD_PX) * scale,
      picked,
    };
  }
}

function rectOf(label: PlacedStateLabel): Rect {
  return {
    left: label.screenX - label.widthPx / 2,
    right: label.screenX + label.widthPx / 2,
    top: label.screenY,
    bottom: label.screenY + label.heightPx,
  };
}
